import { useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { OtpStep } from "@/components/OtpStep";

const HERO = "/img/plant.webp";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const raw = params.get("next") || "";
  const next = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/account";
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState(null);

  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await login(mode === "login" ? "/auth/login" : "/auth/register", mode === "login" ? { email: form.email, password: form.password } : form);
      if (res.otp_required) setOtp(res); else nav(next);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(d ? formatApiError(d) : mode === "register"
        ? "We couldn't confirm your registration because the connection was interrupted. Please try signing in with the same email and password first; your account may already have been created."
        : "We couldn't reach the server. Please check your connection and try again.");
    }
    setBusy(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.2fr_1fr] bg-[#090D16]">
      <div className="on-photo relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Chemical plant at night" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#090D16] via-[#090D16]/70 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 max-w-xl">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F58B23] mb-4">Market Intelligence Platform</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Demand. Prices.<br />Capacity. <span className="text-[#03AB9E]">One view.</span>
          </h1>
          <p className="text-slate-300 mt-5 text-base">
            Track chemical demand forecasts, chemical & utility price trends and global plant capacity — fed by your Excel data.
          </p>
        </div>
      </div>
      <div className="flex items-center px-6 md:px-16">
        {otp ? <OtpStep otp={otp} onBack={() => setOtp(null)} onDone={() => nav(next)} /> :
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <Link to="/" className="block mb-10" data-testid="login-logo-link"><Logo className="h-10" /></Link>
          <h2 className="text-2xl font-bold tracking-tight">{mode === "login" ? "Sign in to dashboards" : "Create your account"}</h2>
          <p className="text-sm text-slate-400 mt-1 mb-8">Access dashboards, reports and your account</p>
          <div className="space-y-4">
            {mode === "register" && (
              <>
                <Input data-testid="register-name-input" placeholder="Full name" value={form.name} onChange={set("name")} required className="h-11 bg-slate-900 border-slate-700" />
                <Input data-testid="register-company-input" placeholder="Company" value={form.company} onChange={set("company")} className="h-11 bg-slate-900 border-slate-700" />
                <Input data-testid="register-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className="h-11 bg-slate-900 border-slate-700" />
              </>
            )}
            <Input data-testid="login-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} required className="h-11 bg-slate-900 border-slate-700" />
            <Input data-testid="login-password-input" type="password" placeholder="Password" value={form.password} onChange={set("password")} required className="h-11 bg-slate-900 border-slate-700" />
            {mode === "register" && <p className="text-[11px] text-slate-500" data-testid="register-password-hint">Use your company email. Password: at least 10 characters with a letter and a number.</p>}
            {mode === "login" && <Link to="/forgot-password" data-testid="login-forgot-link" className="block text-right text-xs text-slate-400 hover:text-[#F58B23]">Forgot password?</Link>}
          </div>
          {error && <p className="text-sm text-red-400 mt-3" data-testid="login-error">{error}</p>}
          <button
            data-testid="login-submit-button"
            disabled={busy}
            className="mt-6 w-full h-11 rounded-md bg-[#1074BA] hover:bg-[#0D629E] text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="login-toggle-mode-button"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-4 text-sm text-slate-400 hover:text-[#F58B23] transition-colors"
          >
            {mode === "login" ? "No account? Register" : "Have an account? Sign in"}
          </button>
        </form>}
      </div>
    </div>
  );
}
